<script>
  /**
   * Leer una factura con la cámara.
   *
   * El flujo tiene tres momentos y ninguno se salta:
   *   1. tomar o escoger la foto
   *   2. esperar mientras la IA la lee
   *   3. REVISAR y corregir antes de guardar
   *
   * El tercero es el que importa. La IA se equivoca leyendo fotos, y un monto
   * mal leído que entra solo contamina el histórico sin que nadie se entere.
   */
  import { formatear, interpretar } from '../lib/dinero.js';
  import { TIPO_MOVIMIENTO, crearMovimiento, crearLinea, hoy } from '../lib/db.js';
  import { guardar, guardarVarios } from '../lib/repositorio.js';
  import { prepararImagen, guardarFoto, revisar, aBorrador } from '../lib/comprobante.js';

  let {
    abierta = $bindable(false),
    cuentas = [],
    categorias = [],
    api = null,
    alGuardar = () => {},
  } = $props();

  let etapa = $state('inicio');   // inicio | leyendo | revisar | error
  let error = $state(null);
  let imagen = $state(null);
  let vistaPrevia = $state(null);
  let borrador = $state(null);
  let revision = $state(null);
  let cuentaId = $state(null);
  let guardando = $state(false);
  let entrada = $state(null);   // el <input type=file>, para vaciarlo

  $effect(() => {
    if (cuentas.length && !cuentaId) cuentaId = cuentas[0].id;
  });

  const categoriasHoja = $derived(
    categorias.filter((c) => c.tipo === 'egreso' && c.padre_id && !c.archivada)
  );

  function reiniciar() {
    etapa = 'inicio';
    error = null;
    imagen = null;
    if (vistaPrevia) URL.revokeObjectURL(vistaPrevia);
    vistaPrevia = null;
    borrador = null;
    revision = null;
    guardando = false;
  }

  function cerrar() {
    reiniciar();
    abierta = false;
  }

  async function alEscoger(evento) {
    const archivo = evento.target.files?.[0];
    if (!archivo) return;

    etapa = 'leyendo';
    error = null;
    try {
      imagen = await prepararImagen(archivo);
      vistaPrevia = URL.createObjectURL(imagen.blob);

      if (!api?.configurada) {
        throw new Error(
          'Falta configurar la sincronización. La lectura de facturas necesita ' +
          'el servidor, porque la clave de la IA vive allá y nunca en tu celular.'
        );
      }

      const r = await api.llamar('ocr', {
        imagen_base64: imagen.base64,
        mime: imagen.mime,
      });

      borrador = aBorrador(r.extraido);
      revision = revisar(r.extraido);
      if (!borrador.fecha) borrador.fecha = hoy();
      etapa = 'revisar';
    } catch (e) {
      error = String(e.message || e);
      etapa = 'error';
    } finally {
      // Se limpia para que escoger la MISMA foto otra vez vuelva a disparar
      // el evento. Sin esto, reintentar con la misma imagen no hace nada.
      if (entrada) entrada.value = '';
    }
  }

  /** Los productos que el usuario dejó marcados se guardan como líneas. */
  let incluirProductos = $state(true);

  async function confirmar() {
    if (guardando || !borrador?.monto) return;
    guardando = true;
    try {
      const movimiento = crearMovimiento({
        tipo: borrador.tipo,
        monto: borrador.monto,
        fecha: borrador.fecha,
        hora: borrador.hora,
        concepto: borrador.concepto,
        contraparte: borrador.contraparte,
        referencia: borrador.referencia,
        impuestos: borrador.impuestos,
        propina: borrador.propina,
        cuenta_origen: borrador.tipo === 'ingreso' ? null : cuentaId,
        cuenta_destino: borrador.tipo === 'ingreso' ? cuentaId : null,
        origen_dato: 'ocr',
        // Queda marcado como revisado porque acabas de verlo en pantalla.
        revisado: true,
        dividido: incluirProductos && borrador.productos.length > 1,
      });

      const idFoto = await guardarFoto(imagen.blob, movimiento.id);
      movimiento.adjunto_id = idFoto;
      await guardar('movimientos', movimiento);

      if (incluirProductos && borrador.productos.length) {
        await guardarVarios('lineas', borrador.productos.map((p) =>
          crearLinea({
            movimiento_id: movimiento.id,
            monto: p.valor_total,
            concepto: p.cantidad > 1
              ? `${p.cantidad} x ${p.descripcion}`
              : p.descripcion,
          })
        ));
      }

      if (navigator.vibrate) navigator.vibrate([12, 40, 12]);
      alGuardar(movimiento);
      cerrar();
    } catch (e) {
      error = String(e.message || e);
      etapa = 'error';
    } finally {
      guardando = false;
    }
  }

  function editarMonto(texto) {
    const v = interpretar(texto);
    if (v !== null) borrador.monto = Math.abs(v);
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={cerrar} aria-label="Cerrar">←</button>
      <h1>Leer factura</h1>
    </header>

    <div class="contenido">
      <!-- ── 1. Escoger la foto ── -->
      {#if etapa === 'inicio'}
        <p class="explica">
          Tómale una foto a la factura o al comprobante. La app saca el monto,
          la fecha, el establecimiento y los productos. Tú revisas antes de que
          se guarde.
        </p>

        <label class="zona-foto">
          <!-- `capture="environment"` hace que en el celular abra la cámara
               trasera directamente, sin pasar por la galería. -->
          <input
            bind:this={entrada}
            type="file"
            accept="image/*"
            capture="environment"
            onchange={alEscoger}
          />
          <span class="icono-camara" aria-hidden="true">📷</span>
          <span class="texto-zona">Tomar foto</span>
          <span class="ayuda">o escoger una de la galería</span>
        </label>

        <p class="nota-privacidad">
          La foto se guarda en tu celular. Solo se manda a leer una copia
          reducida, y la clave de la IA nunca sale del servidor.
        </p>

      <!-- ── 2. Leyendo ── -->
      {:else if etapa === 'leyendo'}
        {#if vistaPrevia}
          <img class="previa" src={vistaPrevia} alt="La factura que tomaste" />
        {/if}
        <div class="leyendo">
          <span class="puntos" aria-hidden="true"><i></i><i></i><i></i></span>
          <p>Leyendo la factura…</p>
          <p class="ayuda">Suele tomar unos cinco segundos.</p>
        </div>

      <!-- ── 3. Revisar y corregir ── -->
      {:else if etapa === 'revisar' && borrador}
        {#if revision.problemas.length}
          {#each revision.problemas as p}
            <div class="alerta grave">
              <strong>Ojo con esta imagen</strong>
              <p>{p.texto}</p>
            </div>
          {/each}
        {/if}

        {#each revision.avisos as aviso}
          <div class="alerta">{aviso}</div>
        {/each}

        <label>
          <span class="etiqueta">Monto</span>
          <input
            class="monto-editable cifra"
            type="text"
            inputmode="numeric"
            value={borrador.monto ? formatear(borrador.monto, { simbolo: false }) : ''}
            oninput={(e) => editarMonto(e.target.value)}
          />
        </label>

        <div class="par">
          <label>
            <span class="etiqueta">Fecha</span>
            <div class="fecha-hora">
              <input type="date" bind:value={borrador.fecha} max={hoy()} />
              <input type="time" bind:value={borrador.hora} aria-label="Hora (opcional)" />
            </div>
          </label>
          <label>
            <span class="etiqueta">Cuenta</span>
            <select bind:value={cuentaId}>
              {#each cuentas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
            </select>
          </label>
        </div>

        <label>
          <span class="etiqueta">Dónde</span>
          <input type="text" bind:value={borrador.contraparte} placeholder="Establecimiento" />
        </label>

        <label>
          <span class="etiqueta">Concepto</span>
          <input type="text" bind:value={borrador.concepto} placeholder="En qué fue" />
        </label>

        {#if borrador.impuestos > 0}
          <p class="dato-extra">IVA leído: <strong class="cifra">{formatear(borrador.impuestos)}</strong></p>
        {/if}

        <!-- Los productos, uno por uno -->
        {#if borrador.productos.length}
          <section class="productos">
            <label class="casilla">
              <input type="checkbox" bind:checked={incluirProductos} />
              <span>
                <strong>Guardar los {borrador.productos.length} productos</strong>
                <span class="ayuda">Así puedes ver después en qué se fue exactamente.</span>
              </span>
            </label>

            {#if incluirProductos}
              {#each borrador.productos as p, i}
                <div class="producto">
                  <input
                    class="desc"
                    type="text"
                    bind:value={borrador.productos[i].descripcion}
                    aria-label="Descripción del producto"
                  />
                  <input
                    class="val cifra"
                    type="text"
                    inputmode="numeric"
                    value={formatear(p.valor_total, { simbolo: false })}
                    oninput={(e) => {
                      const v = interpretar(e.target.value);
                      if (v !== null) borrador.productos[i].valor_total = Math.abs(v);
                    }}
                    aria-label="Valor del producto"
                  />
                  <button
                    class="quitar"
                    onclick={() => (borrador.productos = borrador.productos.filter((_, j) => j !== i))}
                    aria-label="Quitar producto"
                  >×</button>
                </div>
              {/each}
            {/if}
          </section>
        {/if}

        {#if vistaPrevia}
          <details class="ver-foto">
            <summary>Ver la foto</summary>
            <img class="previa" src={vistaPrevia} alt="La factura" />
          </details>
        {/if}

        <div class="botones">
          <button class="secundario" onclick={reiniciar}>Otra foto</button>
          <button class="principal" disabled={!borrador.monto || guardando} onclick={confirmar}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>

      <!-- ── Error ── -->
      {:else if etapa === 'error'}
        <div class="alerta grave">
          <strong>No se pudo leer</strong>
          <p>{error}</p>
        </div>
        <div class="botones">
          <button class="secundario" onclick={reiniciar}>Reintentar</button>
          <button class="principal" onclick={cerrar}>Registrar a mano</button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateY(12px) }
    to   { opacity: 1; transform: translateY(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }

  /* ── Zona de foto ── */
  .zona-foto {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--e-1);
    padding: var(--e-10) var(--e-4);
    border: 2px dashed var(--border-strong);
    border-radius: var(--r-lg);
    cursor: pointer;
    transition: border-color var(--d-rapida) var(--ease-estandar),
                background var(--d-rapida) var(--ease-estandar);
  }
  .zona-foto:active { background: var(--bg-sunken); border-color: var(--acento) }
  .zona-foto input { display: none }
  .icono-camara { font-size: 40px }
  .texto-zona { font-weight: var(--w-semibold); font-size: var(--t-lg) }

  .nota-privacidad {
    font-size: var(--t-xs);
    color: var(--ink-3);
    line-height: var(--lh-loose);
    text-align: center;
  }

  /* ── Leyendo ── */
  .leyendo { text-align: center; padding: var(--e-8) 0; color: var(--ink-2) }
  .puntos { display: inline-flex; gap: 6px; margin-bottom: var(--e-3) }
  .puntos i {
    width: 8px;
    height: 8px;
    border-radius: var(--r-full);
    background: var(--acento);
    display: block;
    animation: latir 1.2s var(--ease-suave) infinite;
  }
  .puntos i:nth-child(2) { animation-delay: 0.15s }
  .puntos i:nth-child(3) { animation-delay: 0.3s }
  @keyframes latir {
    0%, 100% { opacity: 0.25; transform: translateY(0) }
    50%      { opacity: 1;    transform: translateY(-5px) }
  }

  .previa {
    width: 100%;
    border-radius: var(--r-md);
    border: 1px solid var(--border);
  }

  /* ── Avisos ── */
  .alerta {
    font-size: var(--t-sm);
    line-height: var(--lh-loose);
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--alerta-suave);
    color: var(--alerta);
  }
  .alerta.grave { background: var(--peligro-suave); color: var(--peligro) }
  .alerta strong { display: block; margin-bottom: 2px }

  /* ── Formulario ── */
  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input[type='text'],
  input[type='date'],
  select {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }

  .monto-editable {
    font-size: var(--t-2xl);
    font-weight: var(--w-bold);
    text-align: center;
  }

  .ayuda { font-size: var(--t-xs); color: var(--ink-3); font-weight: var(--w-regular) }
  .dato-extra { font-size: var(--t-sm); color: var(--ink-2) }

  /* ── Productos ── */
  .productos { display: flex; flex-direction: column; gap: var(--e-2) }
  .casilla { flex-direction: row; gap: var(--e-3); align-items: flex-start }
  .casilla input { width: 20px; height: 20px; margin-top: 2px; flex: 0 0 auto }
  .casilla span { display: flex; flex-direction: column; gap: 2px }

  .producto { display: grid; grid-template-columns: 1fr 96px 32px; gap: var(--e-2) }
  .producto .desc { font-size: var(--t-sm) }
  .producto .val { font-size: var(--t-sm); text-align: right }
  .quitar {
    color: var(--ink-3);
    font-size: var(--t-lg);
    border-radius: var(--r-sm);
  }

  .ver-foto summary {
    font-size: var(--t-sm);
    color: var(--acento);
    cursor: pointer;
    padding: var(--e-2) 0;
  }

  /* ── Botones ── */
  .botones { display: flex; gap: var(--e-2); margin-top: var(--e-2) }
  .secundario {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }
  /* Fecha y hora juntas: el orden dentro del día también cuenta. */
  .fecha-hora { display: flex; flex-direction: column; gap: var(--e-1) }
</style>
